import { findQuoteMatchRange } from '@src/components/content-components/QuoteCard/findQuoteMatchRange';
import { LogManager } from '@src/utility/Logger';
import { SAVED_SEGMENT_WASH_ALPHA } from '@src/utility/savedSegmentColors';
import { css } from 'styled-components';

import type { TimelineMarker } from '@src/components/transcript/hooks/useTimelineMarkers';
import type { OtherVideo, ShareableImageType } from '@src/types.ts/story';
import type { TranscriptElement } from '@src/types.ts/transcript';

const logger = LogManager.getLogger('utility:transcriptQuoteUtils');

export type HighlightRange = {
  id: string;
  start: number;
  end: number;
};

// --- Quote constants & styles ---

export const QUOTE_MATCH_CLASS = 'quote-match';
export const QUOTE_MATCH_ACTIVE_CLASS = 'quote-match-active';

export const quoteHighlightStyles = css`
  &.${QUOTE_MATCH_CLASS},
    &
    .${QUOTE_MATCH_CLASS},
    &.${QUOTE_MATCH_ACTIVE_CLASS},
    &
    .${QUOTE_MATCH_ACTIVE_CLASS} {
    text-decoration: underline;
    text-decoration-color: var(--content-quotes);
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
  }
`;

// --- Clip constants & styles ---

export const CLIP_MATCH_CLASS = 'clip-match';
export const CLIP_MATCH_ACTIVE_CLASS = 'clip-match-active';

export const clipHighlightStyles = css`
  &.${CLIP_MATCH_CLASS},
    &
    .${CLIP_MATCH_CLASS},
    &.${CLIP_MATCH_ACTIVE_CLASS},
    &
    .${CLIP_MATCH_ACTIVE_CLASS} {
    border-bottom: 1px solid var(--content-clips);
  }

  /* When both quote and clip highlights are on the same element,
     show both underlines: quote via text-decoration, clip via border-bottom */
  &.${QUOTE_MATCH_CLASS}.${CLIP_MATCH_CLASS},
    &
    .${QUOTE_MATCH_CLASS}.${CLIP_MATCH_CLASS},
    &.${QUOTE_MATCH_ACTIVE_CLASS}.${CLIP_MATCH_CLASS},
    &
    .${QUOTE_MATCH_ACTIVE_CLASS}.${CLIP_MATCH_CLASS},
    &.${QUOTE_MATCH_CLASS}.${CLIP_MATCH_ACTIVE_CLASS},
    &
    .${QUOTE_MATCH_CLASS}.${CLIP_MATCH_ACTIVE_CLASS},
    &.${QUOTE_MATCH_ACTIVE_CLASS}.${CLIP_MATCH_ACTIVE_CLASS},
    &
    .${QUOTE_MATCH_ACTIVE_CLASS}.${CLIP_MATCH_ACTIVE_CLASS} {
    text-decoration: underline;
    text-decoration-color: var(--content-quotes);
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
    border-bottom: 1px solid var(--content-clips);
  }
`;

// --- Overlay constants & styles (PLAT-10045) ---

export const OVERLAY_MATCH_CLASS = 'overlay-match';
export const OVERLAY_MATCH_ACTIVE_CLASS = 'overlay-match-active';

/*
 * Overlay underline uses the `--content-overlays` design-system token
 * (navy `#1e3a8a`) — deeper than `--content-quotes` blue so overlay and
 * quote underlines stay visually distinct when they coexist on the same
 * span.
 *
 * When a span has both `quote-match` and `overlay-match` classes,
 * `text-decoration-color` accepts only one value and source-order wins
 * at equal specificity. The consuming styled-components inject
 * `overlayHighlightStyles` AFTER `quoteHighlightStyles`, so overlay
 * (navy) wins the combined case automatically — no explicit combined
 * rule needed. Quote remains navigable via the existing showQuotes
 * toolbar. Clip border-bottom stacks visually on top regardless.
 *
 * `OVERLAY_MATCH_ACTIVE_CLASS` is reserved for future next/prev
 * navigation parity with quote/clip; v1 never applies the class (the
 * apply/clear wrappers carry it through to keep the family signature
 * symmetric) so it intentionally has no rule here.
 */
export const overlayHighlightStyles = css`
  &.${OVERLAY_MATCH_CLASS}, & .${OVERLAY_MATCH_CLASS} {
    text-decoration: underline;
    text-decoration-color: var(--content-overlays);
    text-decoration-thickness: 2px;
    text-underline-offset: 3px;
    /* Keep the underline continuous across the whole overlay range —
       \`text-decoration-skip-ink: auto\` (browser default) breaks the line
       around descenders (g / p / y) and creates visible gaps between
       words. \`none\` draws straight through, matching the connected
       look the design calls for. */
    text-decoration-skip-ink: none;
  }
  /* Brighten the underline for the SPECIFIC overlay range the cursor is
     hovering — applied as a JS-driven \`OVERLAY_MATCH_ACTIVE_CLASS\` on
     spans inside the hovered range (see useHighlightHoverPopover /
     Transcript wiring). Per-range, not per-span: hovering one word in a
     multi-word overlay lights up the whole range, and other overlay
     ranges in the document stay navy. */
  &.${OVERLAY_MATCH_ACTIVE_CLASS}, & .${OVERLAY_MATCH_ACTIVE_CLASS} {
    text-decoration-color: var(--content-overlays-hover);
  }
`;

// --- Saved-segment constants & styles ---

export const SAVED_SEGMENT_MATCH_CLASS = 'saved-segment-match';
export const SAVED_SEGMENT_MATCH_ACTIVE_CLASS = 'saved-segment-match-active';

/*
 * Highlights (the save-for-later → combine flow) render as a marker-style
 * translucent background fill behind the text — distinct from clips' emerald
 * border-bottom and quotes'/overlays' blue underlines, so a highlight can
 * coexist with any of those on the same span without competing for the single
 * `text-decoration-color`. Whitespace elements between words are their own
 * indexed spans and receive the class too, so the fill reads as one continuous
 * band across the range. The violet `--content-saved-segments-bg` token (tied
 * to the purple "Combined Clips" reel badge) is the fallback for legacy
 * highlights saved before per-segment colors; `applySavedSegmentHighlights`
 * overrides it inline per group color. The ACTIVE class is reserved for
 * hover/next-prev parity (carried through the wrappers for a symmetric family
 * signature); v1 only uses the base class.
 */
export const savedSegmentHighlightStyles = css`
  &.${SAVED_SEGMENT_MATCH_CLASS},
    &
    .${SAVED_SEGMENT_MATCH_CLASS},
    &.${SAVED_SEGMENT_MATCH_ACTIVE_CLASS},
    &
    .${SAVED_SEGMENT_MATCH_ACTIVE_CLASS} {
    background-color: var(--content-saved-segments-bg);
  }
`;

// --- Range finders ---

/**
 * Find transcript element index ranges for each shareable image (pull quote).
 * Uses fuzzy matching via findQuoteMatchRange. Returns ranges sorted by start index.
 */
export function findQuoteRanges(
  elements: TranscriptElement[],
  quotes: ShareableImageType[],
): HighlightRange[] {
  const ranges: HighlightRange[] = [];

  for (const quote of quotes) {
    if (!quote.quote?.trim()) continue;
    const match = findQuoteMatchRange(elements, quote.quote);
    if (match) {
      ranges.push({ id: quote.id, start: match.start, end: match.end });
    }
  }

  ranges.sort((a, b) => a.start - b.start);
  return ranges;
}

/**
 * Extract transcript element index ranges from clips that have transcriptPosition.
 * The position is stored in clipJson.transcriptPosition on each OtherVideo.
 * Returns ranges sorted by start index.
 */
export function findClipRanges(clips: OtherVideo[]): HighlightRange[] {
  const ranges: HighlightRange[] = [];

  for (const clip of clips) {
    const pos = clip.clipJson?.transcriptPosition;
    if (!pos) continue;
    const { startIndex, endIndex } = pos;
    if (startIndex != null && endIndex != null && clip.id) {
      ranges.push({ id: clip.id, start: startIndex, end: endIndex });
    }
  }

  ranges.sort((a, b) => a.start - b.start);
  return ranges;
}

/** One run of consecutive array positions an anchor resolves to. */
type AnchorRun = { start: number; end: number; minInitialIndex: number };

/**
 * The array-position run a `transcriptAnchor` resolves to.
 *
 * The anchor is stored as a **source-index window** (`[startInitialIndex,
 * endInitialIndex]`), and the transcript array is ordered by **timeline time**
 * (`TranscriptTrack.fromVideoTrackAndTranscript` sorts segments by
 * `element.time`). Those two agree only while playback order matches source
 * order. Reorder a section on the timeline and `initial_index` stops being
 * monotonic across the array, so the anchored passage can land in two
 * non-adjacent regions — and taking the first and last matching position, as
 * this did, produced a range that swallowed every unrelated word between them.
 *
 * A graphic occupies ONE contiguous timeline range, so its words must be one
 * contiguous run. This collects the runs and returns the one holding the
 * anchor's **first word in source order** — the word the creator's selection
 * started at, whichever array region it now sits in. The rest of the passage is
 * dropped rather than bridged: an underline (and the AI text read from it) that
 * covers words the graphic does not is worse than one that covers fewer.
 *
 * What ends a run is deliberately narrow: only a **surviving word from outside
 * the window**. Removed/cut words (`ts == null`) and tokens without an
 * `initial_index` are transparent, which is what preserves the long-standing
 * behaviour that deleted words bracketed by survivors stay inside the range as
 * grey strikethroughs.
 *
 * An **inverted** window (`start > end`) matches nothing and resolves to `null`,
 * which is what it already did. Pre-fix capture could write one from a selection
 * spanning a reorder seam, so such anchors may exist in old stories; the graphic
 * then falls back to its time window, which is the correct binding under a
 * reorder anyway. Normalizing to `[min, max]` instead would be a regression —
 * the widened window spans every section between the two words.
 *
 * Single-run anchors (every anchor on a timeline in playback order) take the
 * early return and behave exactly as before.
 */
function resolveAnchorRun(
  elements: TranscriptElement[],
  anchor: { startInitialIndex: number; endInitialIndex: number },
): { start: number; end: number } | null {
  const { startInitialIndex: lo, endInitialIndex: hi } = anchor;

  const runs: AnchorRun[] = [];
  let current: AnchorRun | null = null;
  let startWordRun: AnchorRun | null = null;

  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    const ts =
      typeof (el as { ts?: unknown }).ts === 'number'
        ? (el as { ts: number }).ts
        : null;
    if (ts == null) continue; // removed/cut word (see CLAUDE.md #12) — transparent
    const initialIndex = (el as { initial_index?: unknown }).initial_index;
    if (typeof initialIndex !== 'number') continue; // untracked token — transparent
    if (initialIndex < lo || initialIndex > hi) {
      // A surviving word from outside the anchored passage: the one thing that
      // closes a run, and after a reorder the thing that separates its halves.
      current = null;
      continue;
    }
    if (current) {
      current.end = i;
      if (initialIndex < current.minInitialIndex) {
        current.minInitialIndex = initialIndex;
      }
    } else {
      current = { start: i, end: i, minInitialIndex: initialIndex };
      runs.push(current);
    }
    if (initialIndex === lo) startWordRun = current;
  }

  if (runs.length === 0) return null;
  if (runs.length === 1) return { start: runs[0].start, end: runs[0].end };

  // The anchor's own start word wins. It is absent only when that exact word was
  // cut, in which case the earliest surviving word of the passage stands in.
  const chosen =
    startWordRun ??
    runs.reduce((a, b) => (b.minInitialIndex < a.minInitialIndex ? b : a));
  // `debug`, not `warn`: this runs on every render for as long as the split
  // persists, and a per-render warn is exactly the log spam CLAUDE.md forbids.
  logger.debug('Anchored overlay spans a reordered section boundary', {
    startInitialIndex: lo,
    endInitialIndex: hi,
    runs: runs.length,
    chosen: { start: chosen.start, end: chosen.end },
  });
  return { start: chosen.start, end: chosen.end };
}

/**
 * Flatten named saved-segment GROUPS (`story.savedSegments`, the
 * save-for-later → combine flow) into highlight ranges. Each group holds one
 * or more ranges; every range is emitted tagged with the group's epoch-ms key
 * as its id (so the hover popover resolves the whole group from any of its
 * ranges). Out-of-bounds ranges (indices stale after a transcript edit) are
 * skipped so they simply don't render — matching how clips behave when their
 * `transcriptPosition` drifts. Returns ranges sorted by start index.
 */
export function findSavedSegmentRanges(
  segments:
    | Record<number, { ranges: { startIndex: number; endIndex: number }[] }>
    | undefined,
  elementsLength: number,
): HighlightRange[] {
  const ranges: HighlightRange[] = [];
  if (!segments) return ranges;

  for (const [key, group] of Object.entries(segments)) {
    for (const { startIndex, endIndex } of group.ranges ?? []) {
      if (
        startIndex == null ||
        endIndex == null ||
        startIndex < 0 ||
        endIndex >= elementsLength ||
        startIndex > endIndex
      ) {
        continue;
      }
      ranges.push({ id: key, start: startIndex, end: endIndex });
    }
  }

  ranges.sort((a, b) => a.start - b.start);
  return ranges;
}

/**
 * PLAT-10949 — Convert highlight element-index ranges into the time windows
 * the video player should limit playback to (sorted by start, overlapping /
 * touching windows merged so playback never jumps backwards). `ts`/`end_ts`
 * are null at runtime for removed elements (see "Deleted Element Timestamps"
 * in CLAUDE.md) and absent on punct/whitespace, so each edge scans inward for
 * the first usable timestamp; ranges with no timed words are skipped.
 */
export function highlightRangesToPlaybackRanges(
  ranges: HighlightRange[],
  elements: TranscriptElement[] | undefined,
): { start: number; end: number }[] {
  if (!elements || elements.length === 0) return [];

  const windows: { start: number; end: number }[] = [];
  for (const range of ranges) {
    let start: number | null = null;
    let end: number | null = null;
    for (let i = range.start; i <= range.end && i < elements.length; i++) {
      const el = elements[i];
      if (el.type === 'text' && el.ts != null) {
        start = el.ts;
        break;
      }
    }
    for (
      let i = Math.min(range.end, elements.length - 1);
      i >= range.start;
      i--
    ) {
      const el = elements[i];
      if (el.type === 'text' && el.end_ts != null) {
        end = el.end_ts;
        break;
      }
    }
    if (start == null || end == null || end <= start) continue;
    windows.push({ start, end });
  }

  windows.sort((a, b) => a.start - b.start);
  const merged: { start: number; end: number }[] = [];
  for (const w of windows) {
    const last = merged[merged.length - 1];
    // 50ms tolerance: back-to-back highlights shouldn't produce a zero-length
    // "gap" jump between them.
    if (last && w.start <= last.end + 0.05) {
      last.end = Math.max(last.end, w.end);
    } else {
      merged.push({ ...w });
    }
  }
  return merged;
}

/**
 * PLAT-10045 — Given non-card composition placements (overlay / other /
 * quoteCard) on the clip timeline, find the transcript element index
 * ranges each one covers. An element is "covered" when its [ts, end_ts)
 * timestamp range overlaps the overlay's [time, endTime).
 *
 * Elements with `ts == null` (removed/cut, see "Deleted Element Timestamps"
 * in CLAUDE.md) do not extend coverage on their own — i.e. a deleted
 * word can't open a range or move `end` forward. They are not "subtracted"
 * from a range bracketed by surviving words on either side: the resulting
 * [start, end] index range remains contiguous through the deleted word's
 * index. This matches the underline rendering — deleted words show as
 * grey strikethroughs but stay visually grouped within the overlay range.
 *
 * Returns ranges sorted by start index.
 */
export function findOverlayRanges(
  overlays: TimelineMarker[],
  elements: TranscriptElement[],
): HighlightRange[] {
  const ranges: HighlightRange[] = [];
  for (const overlay of overlays) {
    // Anchored overlays (added from a transcript selection) map by the words'
    // STABLE `initial_index`, not the overlay's fixed time window. Ripple edits
    // shift word timestamps but never their `initial_index`, so the underline
    // stays on exactly the anchored words — it can't drift onto neighbours as a
    // time-window mapping does once the surrounding words slide. Endpoints snap
    // inward to the nearest surviving word; deleted words bracketed by survivors
    // stay inside the (contiguous) range as grey strikethroughs. See
    // `resolveAnchorRun` for what a reordered section does to that mapping.
    if (overlay.anchor) {
      const run = resolveAnchorRun(elements, overlay.anchor);
      if (run) {
        ranges.push({ id: overlay.id, start: run.start, end: run.end });
      }
      continue;
    }

    let start = -1;
    let end = -1;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i];
      const ts =
        typeof (el as { ts?: unknown }).ts === 'number'
          ? (el as { ts: number }).ts
          : null;
      if (ts == null) continue;
      const endTs =
        typeof (el as { end_ts?: unknown }).end_ts === 'number'
          ? (el as { end_ts: number }).end_ts
          : ts;
      if (ts >= overlay.endTime) break;
      // Membership by the word's MIDPOINT at the start boundary, not any
      // temporal overlap. Adjacent transcript words routinely have touching /
      // overlapping timestamps (word[n].end_ts >= word[n+1].ts), so the old
      // `endTs <= overlay.time` test underlined the word BEFORE the overlay's
      // start word — e.g. "loads" leaking into an overlay anchored at "right".
      const mid = (ts + endTs) / 2;
      if (mid < overlay.time) continue;
      if (start === -1) start = i;
      end = i;
    }
    if (start !== -1 && end !== -1) {
      ranges.push({ id: overlay.id, start, end });
    }
  }
  // Output is sorted by start index (PLAT-10066). `findRangeIdForIndex`
  // relies on start-ascending input for its first-wins overlap semantics,
  // and `findTranslatedOverlayRanges` consumes these ranges directly.
  // Existing PLAT-10045 callers are order-insensitive (they expand every
  // range via `rangesToIndices`), so the sort is a safe, deterministic add.
  ranges.sort((a, b) => a.start - b.start);
  return ranges;
}

/**
 * Minimal shape of a translated phrase needed for overlay range mapping —
 * a phrase-lead key (the value the translated DOM stamps on
 * `data-initial-index`) and the array of source-element `initial_index`
 * positions the phrase aggregates from. The lead is always the smallest
 * value in `originalIndices` (see `useTranslatedRender`).
 */
export interface TranslatedPhraseSourceInfo {
  lead: number;
  originalIndices: ReadonlyArray<number>;
}

/**
 * PLAT-10066 — Translated-view counterpart to `findOverlayRanges`,
 * anchored in **source-index space** rather than time.
 *
 * The translated transcript renders one span per phrase, keyed by
 * `data-initial-index={phrase.lead}`. Each phrase aggregates multiple
 * source elements; its `[startTs, endTs]` window can be much wider than
 * any individual source word. A naive time-overlap test (the v1 of this
 * function) lets two distinct source-language overlays each claim the
 * same boundary-straddling phrase, and the resulting lead-space ranges
 * touch / merge — visually rendering as one continuous underline even
 * when the original-language view shows two distinct underlines.
 *
 * Fix: take the pre-computed source-language ranges (from
 * `findOverlayRanges`) and assign each phrase to **at most one** source
 * range via best-fit on `originalIndices` intersection:
 *
 *   - Count how many of the phrase's `originalIndices` fall inside each
 *     source range.
 *   - The range with the highest count wins. Ties broken by the earlier
 *     source range (smaller `start`).
 *   - A phrase whose indices don't intersect any source range stays
 *     unassigned (no underline).
 *
 * Result: each source range's translated lead-space range covers only the
 * phrases that primarily came from its source span. Boundary phrases go
 * to one side (the side they overlap with most), so two ranges
 * cannot share a lead — preventing the merge.
 *
 * One source range in, one lead-space range out. Ids are NOT the grouping
 * key: a saved-segment highlight group emits one range per passage under a
 * single group key, and collapsing those by id produced one span reaching
 * from the group's first passage to its last (PLAT-11192).
 *
 * Phrases must be passed in lead-ascending order so that the per-range
 * [start, end] computation is correct on first-encounter / last-encounter.
 * `buildTranslatedPhraseTimeInfos` returns phrases in that order.
 */
export function findTranslatedOverlayRanges(
  sourceRanges: ReadonlyArray<HighlightRange>,
  phrases: ReadonlyArray<TranslatedPhraseSourceInfo>,
): HighlightRange[] {
  if (sourceRanges.length === 0 || phrases.length === 0) return [];

  // First pass: best-fit assign each phrase to one source range, tracked by
  // that range's POSITION in the input — not by its id. Ids are not unique:
  // a saved-segment highlight group emits one range per passage, all tagged
  // with the group key (see `findSavedSegmentRanges`), so collapsing by id
  // merged a group's separate passages into one span covering everything
  // between them (PLAT-11192).
  const phraseAssignment = new Map<number, number>(); // phrase.lead → range index
  for (const p of phrases) {
    let bestIndex = -1;
    let bestOverlap = 0;
    let bestStart = Number.POSITIVE_INFINITY;
    for (let r = 0; r < sourceRanges.length; r++) {
      const range = sourceRanges[r];
      let overlap = 0;
      for (const idx of p.originalIndices) {
        if (idx >= range.start && idx <= range.end) overlap++;
      }
      if (overlap === 0) continue;
      if (
        overlap > bestOverlap ||
        (overlap === bestOverlap && range.start < bestStart)
      ) {
        bestOverlap = overlap;
        bestStart = range.start;
        bestIndex = r;
      }
    }
    if (bestIndex !== -1) phraseAssignment.set(p.lead, bestIndex);
  }

  // Second pass: collapse to [minLead, maxLead] per SOURCE RANGE.
  //
  // The [min, max] collapse (and the `rangesToIndices` expansion that
  // consumes it) is safe ONLY when the source ranges are disjoint in
  // source-index space — the normal case for both callers: overlays are
  // temporally distinct, and a highlight's passages are separate selections.
  // Under that invariant a lead that sits strictly between two leads
  // assigned to range A is itself a subset of A's contiguous source
  // span, so best-fit can never hand it to a different range (a
  // competing range can't out-overlap a subset, and ties favor the
  // earlier range). Therefore no foreign lead is ever swept into A's
  // [min, max]. If two source ranges ever covered overlapping source words
  // the ranges could interleave — acceptable, and arguably correct, for that
  // rare shape.
  const groups = new Map<number, { start: number; end: number }>();
  for (const p of phrases) {
    const rangeIndex = phraseAssignment.get(p.lead);
    if (rangeIndex === undefined) continue;
    const cur = groups.get(rangeIndex);
    if (!cur) {
      groups.set(rangeIndex, { start: p.lead, end: p.lead });
    } else {
      if (p.lead < cur.start) cur.start = p.lead;
      if (p.lead > cur.end) cur.end = p.lead;
    }
  }

  const ranges: HighlightRange[] = [];
  for (const [rangeIndex, range] of groups) {
    ranges.push({
      id: sourceRanges[rangeIndex].id,
      start: range.start,
      end: range.end,
    });
  }
  ranges.sort((a, b) => a.start - b.start);
  return ranges;
}

/**
 * PLAT-10120 — Resolve which overlay range ids should carry the brighten
 * (`OVERLAY_MATCH_ACTIVE_CLASS`) treatment, given the current canvas/timeline
 * selection and the currently-hovered overlay id.
 *
 * The result is the union of selected + hovered, **intersected with the ids
 * that actually have an overlay range**. This matters because the selection
 * set passed in (`VideoCreatorStore.activeElementIds`) contains *every*
 * selected element — video clips, images, captions — not just overlays. Only
 * ids that map to a real overlay range brighten; everything else is ignored.
 */
export function resolveActiveOverlayRangeIds(
  overlayRanges: ReadonlyArray<HighlightRange>,
  selectedIds: ReadonlyArray<string> | undefined,
  hoveredId: string | null,
): Set<string> {
  const selected = new Set(selectedIds ?? []);
  const active = new Set<string>();
  for (const r of overlayRanges) {
    if (selected.has(r.id) || r.id === hoveredId) active.add(r.id);
  }
  return active;
}

/**
 * Find the index of the first range whose start element is visible within the
 * scroll container's viewport. Returns -1 if none are visible.
 */
export function findFirstVisibleRangeIndex(
  container: HTMLElement,
  ranges: HighlightRange[],
  scrollContainer: HTMLElement,
): number {
  const scrollRect = scrollContainer.getBoundingClientRect();
  for (let i = 0; i < ranges.length; i++) {
    const el = container.querySelector<HTMLElement>(
      `[data-index='${ranges[i].start}']`,
    );
    if (el) {
      const elRect = el.getBoundingClientRect();
      if (elRect.top >= scrollRect.top && elRect.top <= scrollRect.bottom) {
        return i;
      }
    }
  }
  return -1;
}

// --- Generic highlight utilities ---

/**
 * Flatten ranges into a deduplicated, sorted array of all element indices.
 */
export function rangesToIndices(ranges: HighlightRange[]): number[] {
  const indexSet = new Set<number>();
  for (const range of ranges) {
    for (let i = range.start; i <= range.end; i++) {
      indexSet.add(i);
    }
  }
  return Array.from(indexSet).sort((a, b) => a - b);
}

/**
 * Apply highlight CSS classes to word spans within a container.
 * The active range receives an additional class and its first element is scrolled into view.
 *
 * `attrName` defaults to `'data-index'` (original-language source-element
 * index). Pass `'data-initial-index'` for translated views, where spans
 * are keyed by phrase-lead `initial_index` — see PLAT-10066. The same
 * apply / clear loop walks integer keys; the attribute name is the only
 * thing that varies between the two surfaces.
 *
 * NOTE: this matches one span PER WORD INDEX. It is correct only for views
 * that render a span per word (TranscriptionText / the translated views). The
 * strikethrough/diff view (`_TranscriptionDiff`) collapses a run of same-state
 * words into a single block span, so a per-span CSS class can't underline a
 * sub-range of a run — that view underlines overlay ranges at render time
 * instead (see Transcript overlay handling). Do not "generalize" this to match
 * block spans by range overlap: a multi-word run would then light up whole.
 */
export function applyHighlights(
  container: HTMLElement,
  allIndices: number[],
  activeRange: HighlightRange | null,
  matchClass: string,
  activeClass: string,
  scrollContainer?: HTMLElement,
  attrName: string = 'data-index',
  // PLAT-10611 (F5, R9) — DEFENSIVE ONLY. Maps a translated lead `initial_index`
  // to its stable element UUID. When supplied, each in-range position is queried
  // by `data-source-uuid` FIRST (drift-correct), falling back to `[attrName]`
  // (§6). The translated (`data-initial-index`) branch of this iterator is GATED
  // OFF at runtime (`Transcript.tsx`: the translated overlay underline is painted
  // by the render-time `data-overlay-id` path instead), so this changes NO live
  // behaviour — it only makes the shared loop id-capable if that branch is ever
  // re-enabled. The live `data-index` quote/clip path passes no map → unchanged.
  leadUuidByIndex?: ReadonlyMap<number, string>,
): void {
  const queryByIndex = (i: number): HTMLElement | null => {
    const uuid = leadUuidByIndex?.get(i);
    if (uuid) {
      const byId = container.querySelector<HTMLElement>(
        `[data-source-uuid='${uuid}']`,
      );
      if (byId) return byId;
    }
    return container.querySelector<HTMLElement>(`[${attrName}='${i}']`);
  };

  for (const idx of allIndices) {
    const el = queryByIndex(idx);
    if (el) el.classList.add(matchClass);
  }

  if (activeRange) {
    let scrollTarget: HTMLElement | null = null;
    for (let i = activeRange.start; i <= activeRange.end; i++) {
      const el = queryByIndex(i);
      if (el) {
        el.classList.add(activeClass);
        if (!scrollTarget) scrollTarget = el;
      }
    }
    if (scrollTarget) {
      const scrollable = scrollContainer || container;
      const targetRect = scrollTarget.getBoundingClientRect();
      const scrollableRect = scrollable.getBoundingClientRect();
      const isInView =
        targetRect.top >= scrollableRect.top &&
        targetRect.bottom <= scrollableRect.bottom;
      if (!isInView) {
        const offsetInScrollable =
          targetRect.top - scrollableRect.top + scrollable.scrollTop;
        scrollable.scrollTo({
          top: offsetInScrollable - scrollable.clientHeight / 3,
          behavior: 'smooth',
        });
      }
    }
  }
}

/** Remove highlight classes from word spans within a container. */
export function clearHighlights(
  container: HTMLElement,
  matchClass: string,
  activeClass: string,
): void {
  container
    .querySelectorAll(`.${matchClass}`)
    .forEach(el => el.classList.remove(matchClass));
  container
    .querySelectorAll(`.${activeClass}`)
    .forEach(el => el.classList.remove(activeClass));
}

// --- Convenience wrappers for quotes (backward compat) ---

export function applyQuoteHighlights(
  container: HTMLElement,
  allIndices: number[],
  activeRange: HighlightRange | null,
  scrollContainer?: HTMLElement,
): void {
  applyHighlights(
    container,
    allIndices,
    activeRange,
    QUOTE_MATCH_CLASS,
    QUOTE_MATCH_ACTIVE_CLASS,
    scrollContainer,
  );
}

export function clearQuoteHighlights(container: HTMLElement): void {
  clearHighlights(container, QUOTE_MATCH_CLASS, QUOTE_MATCH_ACTIVE_CLASS);
}

// --- Convenience wrappers for clips ---

export function applyClipHighlights(
  container: HTMLElement,
  allIndices: number[],
  activeRange: HighlightRange | null,
  scrollContainer?: HTMLElement,
): void {
  applyHighlights(
    container,
    allIndices,
    activeRange,
    CLIP_MATCH_CLASS,
    CLIP_MATCH_ACTIVE_CLASS,
    scrollContainer,
  );
}

export function clearClipHighlights(container: HTMLElement): void {
  clearHighlights(container, CLIP_MATCH_CLASS, CLIP_MATCH_ACTIVE_CLASS);
}

// --- Convenience wrappers for saved segments ---

/**
 * Apply saved-segment highlights, coloring each range by its group's color.
 * Unlike the generic applier, this sets a translucent `background-color` INLINE
 * per span (keyed off `range.id` → `colorByKey`) so multiple highlights can
 * show distinct marker colors with the same fill style. The group color is
 * washed via `color-mix` at `SAVED_SEGMENT_WASH_ALPHA`, matching the
 * `--content-saved-segments-bg` token's alpha so the text stays readable.
 * Ranges whose group has no color
 * (highlights saved before per-segment colors) get only the class, so the CSS
 * `--content-saved-segments-bg` violet applies as a fallback.
 *
 * Returns the FIRST span it actually washed, or null when no in-range span was
 * in the DOM. Callers that need to notice the wash being dropped (a remount
 * strips the class, since the spans render `className=""`) hold on to that
 * node and check `isConnected` — cheaper and more precise than re-querying,
 * and only this function knows which indices were really present. See
 * `useSavedSegmentWash` (PLAT-11192).
 */
export function applySavedSegmentHighlights(
  container: HTMLElement,
  ranges: HighlightRange[],
  colorByKey: Record<string, string>,
  attrName: string = 'data-index',
): HTMLElement | null {
  let firstWashed: HTMLElement | null = null;
  for (const range of ranges) {
    const color = colorByKey[range.id];
    for (let i = range.start; i <= range.end; i++) {
      const el = container.querySelector<HTMLElement>(`[${attrName}='${i}']`);
      if (!el) continue;
      el.classList.add(SAVED_SEGMENT_MATCH_CLASS);
      firstWashed ??= el;
      if (color) {
        const alpha = Math.round(SAVED_SEGMENT_WASH_ALPHA * 100);
        el.style.backgroundColor = `color-mix(in srgb, ${color} ${alpha}%, transparent)`;
      }
    }
  }
  return firstWashed;
}

export function clearSavedSegmentHighlights(container: HTMLElement): void {
  // Drop the inline color applied above before removing the class so a stale
  // color never lingers on a span that's no longer part of a saved segment.
  container
    .querySelectorAll<HTMLElement>(`.${SAVED_SEGMENT_MATCH_CLASS}`)
    .forEach(el => {
      el.style.backgroundColor = '';
    });
  clearHighlights(
    container,
    SAVED_SEGMENT_MATCH_CLASS,
    SAVED_SEGMENT_MATCH_ACTIVE_CLASS,
  );
}

// --- Convenience wrappers for overlays (PLAT-10045) ---

export function applyOverlayHighlights(
  container: HTMLElement,
  allIndices: number[],
  activeRange: HighlightRange | null,
  scrollContainer?: HTMLElement,
  attrName: string = 'data-index',
): void {
  applyHighlights(
    container,
    allIndices,
    activeRange,
    OVERLAY_MATCH_CLASS,
    OVERLAY_MATCH_ACTIVE_CLASS,
    scrollContainer,
    attrName,
  );
}

export function clearOverlayHighlights(container: HTMLElement): void {
  clearHighlights(container, OVERLAY_MATCH_CLASS, OVERLAY_MATCH_ACTIVE_CLASS);
}

// --- Active-overlay brighten (PLAT-10120 hover / PLAT-10122 selection) ---

/**
 * PLAT-10120 / PLAT-10122 — brighten the spans inside every *active* overlay
 * range (the union of hovered + canvas/timeline-selected overlays, as resolved
 * by {@link resolveActiveOverlayRangeIds}). Tags each in-range span with
 * `OVERLAY_MATCH_ACTIVE_CLASS` only — the base `OVERLAY_MATCH_CLASS` underline
 * is owned by the render pass, so this never adds or removes the navy
 * underline, just the brighter active treatment. Clears any previously-active
 * spans first so it is safe to call on every selection/hover change.
 *
 * `attrName` defaults to `'data-index'`; pass `'data-initial-index'` for the
 * translated view (phrase-lead keyed) — see PLAT-10066.
 */
export function applyActiveOverlayBrighten(
  container: HTMLElement,
  overlayRanges: ReadonlyArray<HighlightRange>,
  activeIds: ReadonlySet<string>,
  attrName: string = 'data-index',
  // PLAT-10611 (F5, R10) — DEFENSIVE ONLY (see `applyHighlights`): id-first when
  // supplied. The translated branch is dead at runtime, so this is inert live.
  leadUuidByIndex?: ReadonlyMap<number, string>,
): void {
  clearActiveOverlayBrighten(container);
  if (activeIds.size === 0) return;
  for (const range of overlayRanges) {
    if (!activeIds.has(range.id)) continue;
    for (let i = range.start; i <= range.end; i++) {
      const uuid = leadUuidByIndex?.get(i);
      const el =
        (uuid
          ? container.querySelector<HTMLElement>(`[data-source-uuid='${uuid}']`)
          : null) ??
        container.querySelector<HTMLElement>(`[${attrName}='${i}']`);
      if (el) el.classList.add(OVERLAY_MATCH_ACTIVE_CLASS);
    }
  }
}

/** Remove only the active brighten class, leaving the base underline intact. */
export function clearActiveOverlayBrighten(container: HTMLElement): void {
  container
    .querySelectorAll(`.${OVERLAY_MATCH_ACTIVE_CLASS}`)
    .forEach(el => el.classList.remove(OVERLAY_MATCH_ACTIVE_CLASS));
}

/**
 * Redline-view brighten. The strikethrough/diff view renders overlay underlines
 * as `OverlayUnderline` spans tagged `data-overlay-id` (NOT per-word
 * `data-index` spans), so the index-based {@link applyActiveOverlayBrighten}
 * can't reach them — and worse, applied to a removed word's block span it would
 * recolor that span's `line-through` (the red strikethrough) instead of the
 * navy underline. This brightens the underline spans directly by overlay id,
 * leaving the strikethrough untouched.
 */
export function brightenOverlayUnderlinesById(
  container: HTMLElement,
  activeIds: ReadonlySet<string>,
): void {
  clearActiveOverlayBrighten(container);
  if (activeIds.size === 0) return;
  for (const id of activeIds) {
    container
      .querySelectorAll(`[data-overlay-id='${id}']`)
      .forEach(el => el.classList.add(OVERLAY_MATCH_ACTIVE_CLASS));
  }
}
